/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback } from "react";
import { CustomCellRendererProps } from "@ag-grid-community/react";
import { Directions } from "@mui/icons-material";
import { IconButton, Stack, Tooltip } from "@mui/material";

import { getCableSelectFeatureTarget, isActiveSplice, SpliceRecord, SpliceRecordCable } from "fond/topologicalView/types";

import { useTopologicalViewContext } from "./TopologicalViewContext";

type Props = { side: "input" | "output" } & Pick<CustomCellRendererProps<SpliceRecord, SpliceRecordCable>, "value" | "valueFormatted">;

export const formatSpliceRecordCable = ({ value, recordCount }: { value?: SpliceRecordCable | null; recordCount: number | undefined }) => {
  if (value === "dead") {
    return "DEAD";
  }
  if (value === "spare") {
    return recordCount !== undefined ? `SPARE (x${recordCount.toLocaleString()})` : "SPARE";
  }
  return value?.cable.label ?? "";
};

/**
 * Component to render an incoming/outgoing cable cell.
 */
const CableRenderer = ({ value, valueFormatted, side }: Props) => {
  const { selectFeature } = useTopologicalViewContext();

  const hasCable = isActiveSplice(value);
  const hasFeature = hasCable && !!value.cable.id;

  const onClickCable = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      const target = getCableSelectFeatureTarget(value);
      if (!target) return;

      const end = side === "input" ? "upstream" : "downstream";
      selectFeature(target, { zoomTopologicalView: true, highlightFeature: true, zoomMap: true, end });
    },
    [selectFeature, value, side]
  );

  const goToFeature = useCallback(() => {
    const target = getCableSelectFeatureTarget(value);
    if (!target) return;

    selectFeature(target, {
      zoomMap: true,
      highlightFeature: true,
      showProperties: true,
    });
  }, [value, selectFeature]);

  return (
    <Stack justifyContent="center" alignItems="center" direction="row">
      {hasCable ? (
        <a href="#" onClick={onClickCable}>
          {valueFormatted}
        </a>
      ) : (
        valueFormatted
      )}
      {hasFeature && (
        <Tooltip title="View feature" arrow>
          <IconButton size="small" color="primary" onClick={goToFeature}>
            <Directions style={{ fontSize: "18px" }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default CableRenderer;
